import { Capacitor } from "@capacitor/core";
import {
  SENSITIVE_REAUTH_ERROR_CODES,
  reauthenticateForSensitiveAction,
} from "./sensitiveReauthentication";
import {
  AUTH_PROVIDER_STATE,
  getAuthProviderState,
} from "../utils/authProviders";
import { isNativeIosAppleAuthAvailable } from "./appleAuth";

export const ACCOUNT_DELETION_ERROR_CODES = Object.freeze({
  APPLE_AUTHORIZATION_CODE_MISSING: "ACCOUNT_DELETION_APPLE_AUTHORIZATION_CODE_MISSING",
});

export const ACCOUNT_DELETION_IN_PROGRESS_KEY = "vaultquest:accountDeletionInProgress";

export function markAccountDeletionInProgress(uid) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.setItem(
      ACCOUNT_DELETION_IN_PROGRESS_KEY,
      uid ? String(uid) : "1"
    );
  } catch {
    // Session storage can be unavailable in private or restricted browser contexts.
  }
}

export function clearAccountDeletionInProgress(uid) {
  if (typeof window === "undefined") return;

  try {
    const storedUid = window.sessionStorage?.getItem(ACCOUNT_DELETION_IN_PROGRESS_KEY);
    if (!uid || !storedUid || storedUid === "1" || storedUid === String(uid)) {
      window.sessionStorage?.removeItem(ACCOUNT_DELETION_IN_PROGRESS_KEY);
    }
  } catch {
    // Session storage can be unavailable in private or restricted browser contexts.
  }
}

export function isAccountDeletionInProgress(uid) {
  if (typeof window === "undefined") return false;

  try {
    const storedUid = window.sessionStorage?.getItem(ACCOUNT_DELETION_IN_PROGRESS_KEY);
    if (!storedUid) return false;
    return !uid || storedUid === "1" || storedUid === String(uid);
  } catch {
    return false;
  }
}

function accountDeletionError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

let accountDeletionInFlight = null;

export async function deleteAccountWithProviderConfirmation({
  firebaseUser,
  password = "",
  deleteAccount,
  reauthenticate = reauthenticateForSensitiveAction,
  capacitor = Capacitor,
} = {}) {
  if (accountDeletionInFlight) return accountDeletionInFlight;

  accountDeletionInFlight = (async () => {
    let deletionMarked = false;

    const providerState = getAuthProviderState(firebaseUser);
    const nativeAppleAvailable = isNativeIosAppleAuthAvailable(capacitor);
    const requiresAppleRevocation =
      providerState === AUTH_PROVIDER_STATE.APPLE_ONLY ||
      providerState === AUTH_PROVIDER_STATE.PASSWORD_AND_APPLE;

    if (
      requiresAppleRevocation &&
      providerState === AUTH_PROVIDER_STATE.PASSWORD_AND_APPLE &&
      !nativeAppleAvailable
    ) {
      throw accountDeletionError(
        SENSITIVE_REAUTH_ERROR_CODES.APPLE_NATIVE_REQUIRED,
        "Apple-linked account deletion currently requires the VaultQuest iOS app."
      );
    }

    const reauthResult = await reauthenticate({ firebaseUser, password, capacitor });
    if (reauthResult?.cancelled) {
      return { cancelled: true, deleted: false };
    }

    const payload = {};
    if (requiresAppleRevocation) {
      const authorizationCode = String(reauthResult?.authorizationCode || "").trim();
      if (!authorizationCode) {
        throw accountDeletionError(
          ACCOUNT_DELETION_ERROR_CODES.APPLE_AUTHORIZATION_CODE_MISSING,
          "Apple did not return an authorization code for account deletion."
        );
      }
      payload.appleAuthorizationCode = authorizationCode;
    }

    try {
      markAccountDeletionInProgress(firebaseUser?.uid);
      deletionMarked = true;
      const result = await deleteAccount(payload);
      return {
        cancelled: false,
        deleted: true,
        method: reauthResult?.method,
        result,
      };
    } catch (error) {
      if (deletionMarked) {
        clearAccountDeletionInProgress(firebaseUser?.uid);
      }
      throw error;
    }
  })().finally(() => {
    accountDeletionInFlight = null;
  });

  return accountDeletionInFlight;
}
